import { useQuery, type QueryClient } from "@tanstack/react-query";

import { ApiError, apiFetch } from "@/lib/api-client";
import { db } from "@/lib/idb-schema";
import {
  adoptUnattributedEvaluationRows,
  isOwnedBy,
  ownedRow,
  readOwnerIdentity,
} from "@/lib/owner-scope";
import { draftRecordToEvaluationDetail } from "@/lib/draft-to-evaluation-detail";
import type { DraftRecord } from "@/lib/idb-schema-def";
import type { EvaluationDetail } from "@/hooks/queries/evaluation-types";

// Resume read for one evaluation draft (Story 6.7). Mirrors `useListClients`:
// `apiFetch` + `useQuery`, a granular query key, no `skipAuthRefresh` (a
// post-session data call that rides the silent-refresh path). The fetched draft
// hydrates the evaluation-draft store; the steps (6.3/6.4/6.5) read their initial
// values from that store and invalidate THIS key after a save.

/** Root key for every evaluation-detail query. */
export const EVALUATION_QUERY_KEY = "evaluation";

/** Stable, fully-resolved query key so each evaluation caches independently. */
export function evaluationDetailQueryKey(id: string) {
  return [EVALUATION_QUERY_KEY, id] as const;
}

/**
 * Reconcile the cached EvaluationDetail with a just-persisted draft save — the ONE cache
 * contract shared by ScoringStep's save-on-blur and the setup/tasks step-save (gh-459,
 * PR #561 review). An ONLINE commit (`outcome.synced`) patches the acked `version` alongside
 * the exact body it acked — the cache is then server-fresh by construction, so deliberately
 * NO invalidate: an invalidate's refetch GET can observe a save whose store ack is still in
 * flight, read as "strictly newer" by the provider's version-gated rehydrate, and rebuild the
 * draft mid-session (wiping staged scores + stranding the next save on a stale
 * X-Client-Version — the results-flow W1 red of 2026-07-22). An OFFLINE-queued edit never
 * touched the server, so its cached version stays unchanged (body-only patch).
 */
export function reconcileEvaluationDetailCache(
  queryClient: QueryClient,
  id: string,
  body: EvaluationDetail["draft_data"],
  outcome: { synced: boolean; version: number },
): void {
  queryClient.setQueryData<EvaluationDetail>(evaluationDetailQueryKey(id), (old) =>
    old
      ? outcome.synced
        ? { ...old, draft_data: body, version: outcome.version }
        : { ...old, draft_data: body }
      : old,
  );
}

/**
 * On a successful server GET the server is authoritative — write-through to the local `drafts`
 * row so an OFFLINE reopen (gh-459) can reconstruct this eval later: SEED a row when none exists
 * (a resume-of-server eval never touched by the producer), else only BACK-FILL a missing
 * `client_id`. NEVER touch `data`/`version`/`conflict` on an existing row — the provider's
 * version-gated rehydrate owns data reconcile, and clobbering newer local edits (which don't bump
 * the version) or a live conflict pin would be data loss. Best-effort: a local write failure must
 * never fail the query (the server answer still returns).
 *
 * Story 19.6 Unit E (19.6 review) — this is also the SERVER-PROOF ADOPTION point for the pre-v3 rows
 * the v3 upgrade deliberately left unattributed (`upgradeToV3`). Reaching a 200 here means this
 * evaluation came back from `GET /evaluations/{id}` under THIS clinician's token, past the endpoint's
 * `clinician_id` predicate and clinician-only RLS — that is the server stating the evaluation is the
 * caller's, which is exactly the evidence the migration did not have. So an UNATTRIBUTED local row is
 * adopted (with its queued ops and its ledger row) instead of staying invisible and undrainable
 * forever.
 */
async function writeThroughDraftRow(id: string, detail: EvaluationDetail): Promise<void> {
  try {
    const owner = await readOwnerIdentity();
    const stored = await db.drafts.get(id);
    if (stored) {
      if (stored.owner === undefined) {
        // UNATTRIBUTED (pre-v3): the 200 above is the server proving this evaluation is the caller's,
        // so adopt it — draft + its `outbound_queue` ops + its `ledger:*` row, in one transaction
        // (`adoptUnattributedEvaluationRows`), then carry on with the write-through below. With NO
        // resolvable owner there is nothing to adopt it TO, so fail closed and touch nothing.
        if (!owner) return;
        await adoptUnattributedEvaluationRows(id, owner);
      } else if (!ownedRow(stored, owner)) {
        // ATTRIBUTED TO SOMEBODY ELSE — left ENTIRELY alone: neither seeded over, nor back-filled,
        // nor adopted. Adoption is for the ownerless only; reassigning an owned row is the isolation
        // breach itself. (Unreachable in practice — an evaluation id belongs to one clinician, so a
        // successful GET for it means the row is ours — but no write in this unit may reach another
        // clinician's row, and overwriting one would be data loss on top of the breach.)
        return;
      }
    }
    const existing = stored;
    if (!existing) {
      // A-01 (19.6 review) — mirror the guard the adoption path already has (line above):
      // a seed without a resolved owner would be unattributed, hidden from local reads and
      // excluded from the drain. The 200 GET proves the evaluation belongs to this clinician,
      // but without a resolvable owner we have nothing to attribute it TO — fail closed.
      if (!owner) return;
      const seed: DraftRecord = {
        id,
        owner, // stamped at INSERT, so the offline reads above can scope to this clinician
        client_id: detail.client_id,
        version: detail.version,
        last_synced_version: detail.version,
        // `DraftRecord.state` is only `"draft" | "submitted"`, but the server `EvaluationState`
        // has four values (`draft | submitted | scored | archived`). Map draft→draft and EVERY
        // terminal state (submitted/scored/archived) → the terminal `"submitted"` so an offline
        // reopen never treats a locked/scored eval as an editable draft (was: collapse-to-draft).
        state: detail.state === "draft" ? "draft" : "submitted",
        conflict: null,
        data: (detail.draft_data ?? {}) as Record<string, unknown>,
        created_at: detail.created_at,
        updated_at: detail.updated_at ?? detail.created_at,
      };
      await db.drafts.put(seed);
      return;
    }
    if (!existing.client_id && detail.client_id) {
      await db.drafts.update(id, { client_id: detail.client_id });
    }
  } catch {
    // Offline-read hydration is a best-effort convenience — never surface an IDB write error.
  }
}

/**
 * #470b — is there UNSYNCED local work still queued for this eval? An `outbound_queue` op (an
 * offline `create`/`update` not yet drained) means the local `drafts` row is AHEAD of the server
 * by exactly those edits: the offline save writes the full draft body locally but does NOT bump
 * `version`, so `drafts.version === last_synced_version === server.version` and the staleness is
 * invisible to a version check — the queued op is the ONLY signal. Returns the reconstructed local
 * `EvaluationDetail` to serve in place of a stale server GET, or null when the queue is empty (the
 * server is then authoritative). Best-effort: a local-read hiccup never fails the query.
 */
async function pendingLocalDraft(id: string): Promise<EvaluationDetail | null> {
  try {
    // Story 19.6 Unit E — the ops must be the CURRENT clinician's. A previous clinician's queued op
    // for this eval would otherwise make their local body look "ahead" of the authoritative server
    // GET and be served in its place. A pure index `.count()` can no longer answer this (the owner
    // lives in the row, not the index), but `.filter().count()` still streams rows through the
    // predicate instead of materialising them — this runs on EVERY successful GET.
    const owner = await readOwnerIdentity();
    const pending = await db.outbound_queue
      .where("eval_id")
      .equals(id)
      .filter((op) => isOwnedBy(op, owner))
      .count();
    if (pending === 0) return null;
    const row = ownedRow(await db.drafts.get(id), owner);
    return row ? draftRecordToEvaluationDetail(row) : null;
  } catch {
    return null;
  }
}

export function useGetEvaluation(id: string) {
  return useQuery({
    queryKey: evaluationDetailQueryKey(id),
    // gh-459 — network-first, IDB-fallback. ONLINE: the server GET wins (authoritative
    // `client_id`) and write-throughs to the local `drafts` row. OFFLINE: the network call fails,
    // so read the local row and reconstruct the `EvaluationDetail` from it — the route hydrates
    // with NO change to its render tree. `query.isError` now correctly means "neither network nor
    // IDB has it," which surfaces the original error via `ResumeErrorSurface`.
    queryFn: async () => {
      // #472 — snapshot the local draft's server-ack state BEFORE issuing the GET. The 404 fallback
      // decision (below) is made from THIS snapshot, never from a read taken after the round-trip:
      // in the online create race a background drain can ack the create (setting
      // `last_synced_version`) DURING the GET, so a post-fetch read would misclassify a legitimately
      // in-flight create's 404 as a real delete and strand the clinician on "isn't available". A 404
      // means the server lacked the id at GET-issue time ⇒ it was NOT acked before the fetch (a
      // pre-fetch ack would have made the GET return 200, not 404 — the API reads its own
      // strongly-consistent Postgres, no replica lag), so the pre-fetch snapshot is authoritative.
      // Story 19.6 Unit E — every local read in this queryFn is scoped to the clinician using this
      // device (`ownedRow`); a row belonging to the previous clinician reads as ABSENT, so neither
      // the 404 decision below nor the offline fallback can serve their draft.
      const owner = await readOwnerIdentity();
      const preRow = ownedRow(await db.drafts.get(id), owner);
      const wasAckedBeforeFetch = preRow != null && preRow.last_synced_version !== null;
      try {
        const detail = await apiFetch<EvaluationDetail>(`/evaluations/${id}`);
        await writeThroughDraftRow(id, detail);
        // #470b — a successful GET is authoritative ONLY when the device has no unsynced local work
        // for this eval. If an offline `update`/`create` op is still queued, the server draft is
        // STALE by exactly those edits. On a fresh boot with the network back but the queued op not
        // yet drained (the iOS PWA-reclaim reopen), the authoritative GET wins the store's mount-time
        // hydrate and the offline scores VANISH from the UI — even though they remain in the local
        // `drafts` row + the queued op and will still drain. Prefer the local draft until the queue
        // drains; then this branch no-ops and the server GET wins again (reconciled, no staleness).
        const localAhead = await pendingLocalDraft(id);
        if (localAhead) return localAhead;
        return detail;
      } catch (err) {
        // Two DISJOINT reasons to reconstruct from the local `drafts` row instead of surfacing the
        // error — everything else surfaces via `ResumeErrorSurface` (never masked by a stale row):
        //
        //  (a) gh-459 — a genuine TRANSPORT failure (`apiFetch` wraps a fetch reject as `ApiError`
        //      status 0; the 401→offline-mid-refresh path rethrows the same status-0 shape). The
        //      device is offline, so the local draft IS the source of truth — serve ANY local row.
        //
        //  (b) #472 — an in-flight CREATE race. "New evaluation" mints the id on-device, enqueues a
        //      `create` op, fires a background drain, and navigates IMMEDIATELY; online this GET
        //      usually beats the drain's `POST /evaluations/create`, so the server 404s (EVAL-003)
        //      on an id it hasn't persisted yet. Serve the local draft ONLY when it was NEVER acked
        //      by the server AS OF GET-ISSUE TIME (`!wasAckedBeforeFetch` ⇒ the create is still in
        //      flight). If it HAD synced before the fetch a 404 means a real delete/revoke — surface
        //      it (gh-458 intent). The row is RE-READ here for the freshest data to return, but the
        //      DECISION uses the pre-fetch snapshot so an ack landing mid-GET can't flip the verdict.
        //      Reconciliation is automatic: the next refetch (staleTime / post-save invalidation)
        //      hits the now-persisted eval.
        if (err instanceof ApiError && (err.status === 0 || err.status === 404)) {
          // Re-read for freshness, still owner-scoped: offline, the local draft is the source of
          // truth ONLY when it is this clinician's (Story 19.6 Unit E).
          const row = ownedRow(await db.drafts.get(id), owner);
          if (row && (err.status === 0 || !wasAckedBeforeFetch)) {
            return draftRecordToEvaluationDetail(row);
          }
        }
        throw err;
      }
    },
    // A missing/empty id (router not yet resolved) must not fire a request to
    // `/evaluations/` — wait for a real id.
    enabled: Boolean(id),
    // gh-459 (offline-resume completion) — the IDB fallback in `queryFn` above is the WHOLE point
    // of the offline resume, but under the default `networkMode: "online"` React Query PAUSES a
    // query while `navigator.onLine` is false and never runs `queryFn` — so on a genuine offline
    // reload the fallback was unreachable and the route hung on "Loading evaluation…" forever (the
    // gh-459 unit test only faked offline by rejecting `apiFetch` while still online, so it never
    // saw this seam). `"always"` runs `queryFn` regardless of connectivity: online it hits the
    // network, offline `apiFetch` rejects with status 0 and the local draft is served. Scoped to
    // THIS query so no other read starts firing offline.
    networkMode: "always",
    // Don't spin the default 3 retries before the offline user sees their local draft — the
    // queryFn already resolves the IDB fallback in one pass, so a network miss should settle fast.
    // gh-458 review (Issue 2): once the catch is narrowed to transport failures (Issue 1), the ONLY
    // errors that reach here are (a) status-0 transport misses already resolved to the local draft,
    // or (b) real auth/data errors that SHOULD surface immediately without retry churn. Serving the
    // local draft on a transport failure is the intended offline-first behaviour, not a bug, so
    // `retry: false` stays — a retry predicate would add surface for no correctness gain.
    retry: false,
  });
}

/**
 * The header a save-draft call (6.3/6.4/6.5) sends to continue Story 6.2's
 * optimistic-concurrency contract from the RESUMED version:
 * `apiFetch(`/evaluations/${id}/save-draft`, { method: "POST", body, headers: clientVersionHeader(version) })`.
 *
 * 6.7 establishes the carry (the version lives in the draft store); the actual save
 * calls are added by the step stories. The per-call `headers` option on `apiFetch`
 * is the injection seam — no global header infra is needed.
 */
export function clientVersionHeader(version: number): Record<string, string> {
  return { "X-Client-Version": String(version) };
}
